/**
 * Instagram Graph API Integration
 * Documentation: https://developers.facebook.com/docs/instagram-api
 * Requires Business/Creator account
 */

import axios from 'axios';
import config from '../config';

const INSTAGRAM_GRAPH_API = 'https://graph.instagram.com';

class InstagramService {
  constructor() {
    this.cache = new Map();
    this.userId = null;
  }

  /**
   * Get user's Instagram media (posts and reels)
   * @returns {Promise<Array>} Array of media objects
   */
  async getUserMedia() {
    const cacheKey = 'user_media';

    // Check cache
    if (this.isCacheValid(cacheKey)) {
      return this.cache.get(cacheKey).data;
    }

    try {
      // Get user ID if not already fetched
      if (!this.userId) {
        await this.getUserId();
      }

      // Fetch first page of media
      const response = await axios.get(`${INSTAGRAM_GRAPH_API}/${this.userId}/media`, {
        params: {
          fields: 'id,caption,media_type,media_url,thumbnail_url,permalink,timestamp,children{media_url,media_type}',
          limit: config.instagram.maxResults,
          access_token: config.instagram.accessToken,
        },
      });

      let allMedia = response.data.data || [];
      let nextUrl = response.data.paging?.next;
      let page = 1;
      const maxPages = config.instagram.maxPages || 5;

      // Follow pagination to fetch older posts
      while (nextUrl && page < maxPages) {
        try {
          const nextResponse = await axios.get(nextUrl);
          const nextData = nextResponse.data.data || [];
          if (nextData.length === 0) break;
          allMedia = allMedia.concat(nextData);
          nextUrl = nextResponse.data.paging?.next;
          page++;
        } catch (pageError) {
          console.error(`Instagram pagination error on page ${page + 1}:`, pageError.message);
          break;
        }
      }

      const media = allMedia.map(item => this.normalizeMedia(item));

      // Cache the results
      this.setCacheData(cacheKey, media);

      return media;
    } catch (error) {
      console.error('Instagram API Error:', error.response?.data || error.message);
      return this.getFallbackData(cacheKey);
    }
  }

  /**
   * Get user ID from username
   */
  async getUserId() {
    const cacheKey = 'user_id';

    if (this.isCacheValid(cacheKey)) {
      this.userId = this.cache.get(cacheKey).data;
      return this.userId;
    }

    try {
      // For Graph API, we need to get the user ID first
      // This requires the business account ID
      const response = await axios.get(`${INSTAGRAM_GRAPH_API}/me`, {
        params: {
          fields: 'id,username',
          access_token: config.instagram.accessToken,
        },
      });

      this.userId = response.data.id;
      this.setCacheData(cacheKey, this.userId);
      return this.userId;
    } catch (error) {
      console.error('Failed to get Instagram user ID:', error.response?.data || error.message);
      throw error;
    }
  }

  /**
   * Normalize Instagram media to common format
   */
  normalizeMedia(item) {
    const hashtags = this.extractHashtags(item.caption || '');

    return {
      id: item.id,
      platform: 'instagram',
      type: item.media_type.toLowerCase(), // IMAGE, VIDEO, CAROUSEL_ALBUM
      caption: item.caption || '',
      hashtags,
      mediaUrl: item.media_url,
      thumbnailUrl: item.thumbnail_url || item.media_url,
      permalink: item.permalink,
      timestamp: item.timestamp,
      embedUrl: item.permalink,
      isReel: item.media_type === 'VIDEO' && item.caption?.includes('#reels'),
      children: item.children?.data || [],
    };
  }

  /**
   * Extract hashtags from caption
   */
  extractHashtags(caption) {
    const hashtagRegex = /#[\w]+/g;
    const matches = caption.match(hashtagRegex);
    return matches ? matches.map(tag => tag.toLowerCase()) : [];
  }

  /**
   * Filter media by hashtags
   */
  filterByHashtags(media, hashtags) {
    if (!hashtags || hashtags.length === 0) {
      return media;
    }

    return media.filter(item => {
      return hashtags.some(tag =>
        item.hashtags.includes(tag.toLowerCase())
      );
    });
  }

  /**
   * Cache management
   */
  isCacheValid(key) {
    const cached = this.cache.get(key);
    if (!cached) return false;
    return Date.now() - cached.timestamp < config.cache.instagram;
  }

  setCacheData(key, data) {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
    });
  }

  getFallbackData(key) {
    const cached = this.cache.get(key);
    return cached ? cached.data : [];
  }

  /**
   * Clear cache
   */
  clearCache() {
    this.cache.clear();
  }
}

export default new InstagramService();
